<template>
  <BaseModule
    v-bind="$props"
    class="container module-footer remove-padding"
    data-module="footer"
  >
    <footer class="has-text-centered">
      <div
        v-if="logoImg"
        class="logo"
      >
        <EditorLink :model="useLogoLink">
          <LImage
            v-if="logoImg"
            :src="logoImg"
            :alt="logoAltText"
            :no-stretch="true"
            loader-size="40"
          />
        </EditorLink>
      </div>

      <div class="text">
        <div
          v-for="(phone, index) in phones"
          :key="index"
          class="phones"
        >
          <h4>{{ phone.label }}</h4>
          <a
            :href="`tel:${phone.ddi + phone.number.trim()}`"
            class="phone-g"
          >
            <span class="phone-s">{{ phone.ddi }}</span>
            {{ phone.number }}
          </a>
        </div>

        <div v-if="hasAnySocialMedia">
          <h4>{{ $t('socialMedia') }}</h4>
          <ul class="socials__list">
            <li
              v-for="(socialObj, index) in socialLinks"
              :key="index"
              class="socials__list__item"
            >
              <EditorLink
                v-if="showIcon(socialObj)"
                :model="socialObj.linkURL"
                class="socials__list__item__link"
                :title="socialObj.title"
              >
                <component
                  :is="socialObj.socialType"
                  width="40"
                  height="40"
                />
              </EditorLink>
            </li>
          </ul>
        </div>
      </div>
    </footer>
    <div class="bottom">
      <span>
        {{ hotelName }}
      </span>
      <div class="powered-by">
        <span class="mr-3">Powered by</span>
        <a
          href="https://letsbook.com.br"
          target="_blank"
        >
          <IconLogo
            :width="110"
            :height="35"
            color="white"
          />
        </a>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';

import IconFacebook from '@/components/icons/IconFacebook.vue';
import IconInstagram from '@/components/icons/IconInstagram.vue';
import IconYoutube from '@/components/icons/IconYoutube.vue';
import IconLinkedin from '@/components/icons/IconLinkedin.vue';
import IconTripAdvisor from '@/components/icons/IconTripAdvisor.vue';
import IconTwitter from '@/components/icons/IconTwitter.vue';
import IconSpotify from '@/components/icons/IconSpotify.vue';
import IconTikTok from '@/components/icons/IconTikTok.vue';
import IconLogo from '@/components/icons/IconLogo.vue';
import showButton from '@/mixins/showButton';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'Footer',
  components: {
    BaseModule,
    EditorLink,
    facebookLink: IconFacebook,
    instagramLink: IconInstagram,
    youTubeLink: IconYoutube,
    linkedinLink: IconLinkedin,
    tripAdvisorLink: IconTripAdvisor,
    twitterLink: IconTwitter,
    spotifyLink: IconSpotify,
    tiktokLink: IconTikTok,
    IconLogo,
  },
  mixins: [showButton],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    logoAltText: {
      type: String,
      default: '',
    },
    logoImg: {
      type: String,
      default: '',
    },
    hasLogoLink: {
      type: Boolean,
      default: false,
    },
    logoUrl: {
      type: Object,
      default: undefined,
    },
    phones: {
      type: Array,
      default() {
        return [];
      },
    },
    hotelName: {
      type: String,
      default: '',
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    textColor: {
      type: String,
      default: '#ffffff',
    },
    socialLinks: {
      type: Array,
      default() {
        return [];
      },
    },
    paddingTop: {
      type: Number,
      default: 56,
    },
    paddingBottom: {
      type: Number,
      default: 56,
    },
  },
  computed: {
    useLogoLink() {
      return this.hasLogoLink ? this.logoUrl : undefined;
    },
    hasAnySocialMedia() {
      return this.socialLinks.length > 0;
    },
    altLogo() {
      return `Logo de ${this.settings.hotelName}`;
    },
    ...mapGetters(['settings']),
  },
  methods: {
    showIcon(socialObj) {
      return socialObj.socialType && socialObj.linkURL?.value;
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'logo',
            label: 'Logo',
            position: 0,
            fields: [
              {
                name: 'logoImg',
                type: 'ImageFile',
                label: 'Logo',
                maxWidth: 300,
                maxHeight: 90,
                resizeInsteadCrop: true,
                helpText: 'Tamanho máximo 300x90, formato .PNG',
                enableRemoval: true,
              },
              {
                name: 'hasLogoLink',
                type: 'Checkbox',
                label: 'Adicionar link no logo',
              },
              {
                name: 'logoUrl',
                type: 'Url',
                label: 'Link do logo',
                isEnabled({ hasLogoLink }) {
                  return hasLogoLink;
                },
              },
              {
                name: 'logoAltText',
                type: 'ShortText',
                label: 'Texto alternativo',
              },
            ],
          },
          {
            name: 'phones',
            label: 'Telefones',
            fields: [
              {
                name: 'phones',
                type: 'List',
                maxItems: 2,
                itemLabel: 'Telefone {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar telefone',
                fields: [
                  {
                    name: 'label',
                    type: 'ShortText',
                    label: 'Nome do contato',
                    default: '',
                  },
                  {
                    name: 'ddi',
                    type: 'ShortText',
                    label: 'DDI',
                    default: '',
                  },
                  {
                    name: 'number',
                    type: 'ShortText',
                    label: 'Telefone',
                    default: '',
                  },
                ],
              },
            ],
          },
          {
            name: 'socialLinks',
            label: 'Redes Sociais',
            fields: [
              {
                name: 'socialLinks',
                type: 'List',
                itemLabel: 'Link Social {index}',
                addItemLabel: 'Adicionar Link Social',
                fields: [
                  {
                    name: 'socialType',
                    type: 'VueSelect',
                    label: 'Tipo de Link Social',
                    default: '',
                    options: [
                      {
                        label: 'Instagram',
                        value: 'instagramLink',
                      },
                      {
                        label: 'YouTube',
                        value: 'youTubeLink',
                      },
                      {
                        label: 'Facebook',
                        value: 'facebookLink',
                      },
                      {
                        label: 'LinkedIn',
                        value: 'linkedinLink',
                      },
                      {
                        label: 'TripAdvisor',
                        value: 'tripAdvisorLink',
                      },
                      {
                        label: 'Twitter',
                        value: 'twitterLink',
                      },
                      {
                        label: 'Spotify',
                        value: 'spotifyLink',
                      },
                      {
                        label: 'TikTok',
                        value: 'tiktokLink',
                      },
                    ],
                  },
                  {
                    name: 'title',
                    type: 'ShortText',
                    label: 'Texto alternativo (para SEO)',
                    default: '',
                  },
                  {
                    name: 'linkURL',
                    type: 'Url',
                    availableTypes: ['externalLink'],
                  },
                ],
              },
            ],
          },
          {
            name: 'hotelName',
            label: 'Nome do hotel/rede',
            fields: [
              {
                name: 'hotelName',
                type: 'ShortText',
                label: 'Nome',
                helpText:
                  'Essa informação vai aparecer na parte inferior esquerda do Rodapé.',
              },
            ],
          },
        ],
        toRemove: ['text'],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['titleColor', 'buttonTextColor'],
          },
        ],
        toRemove: ['fonts'],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        socialMedia: 'Redes Sociais',
      },
      en: {
        socialMedia: 'social networks',
      },
      es: {
        socialMedia: 'redes sociales',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.module-footer {
  color: var(--text-color);
  background-color: var(--bg-color, var(--secondary-color));

  footer {
    padding: 0 40px;
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);

    @include media-breakpoint-down(md) {
      padding-top: var(--padding-top-mobile);
      padding-bottom: var(--padding-bottom-mobile);
    }

    .text {
      padding-top: 50px;

      @include media-breakpoint-down(lg) {
        white-space: normal;
      }

      & > div {
        display: inline-block;
        vertical-align: middle;
        position: relative;
        width: calc(100% / 3);

        @include media-breakpoint-down(lg) {
          width: 100%;
        }

        & + div {
          @include media-breakpoint-down(lg) {
            margin-top: 50px;
          }
        }

        h4 {
          font-weight: $lb-semibold;
          font-size: $size-normal;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          margin-bottom: 27px;
        }

        a {
          text-decoration: none;
          color: $lb-white;
          line-height: 17px;
        }

        .phone-g {
          font-size: 40px;
        }

        .phone-s {
          font-size: $size-small;
        }
      }

      .phones > a {
        color: var(--button-color, $lb-white);
      }

      .socials {
        &__list {
          &__item {
            display: inline-block;

            &__link {
              width: 40px;
              display: inline-block;
              vertical-align: top;
              margin: 0 8px 16px;
              color: var(--button-color, $lb-white);
            }
          }
        }
      }
    }
  }

  .logo .img {
    max-width: 100%;
  }

  .bottom {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    padding: 21px;
    align-items: center;
    color: var(--text-color, $lb-white);
    text-transform: uppercase;
    font-weight: $lb-semibold;
    background-color: rgba(0, 0, 0, 0.15);

    @include media-breakpoint-down(sm) {
      flex-direction: column;

      span {
        margin-bottom: 20px;
      }
    }

    .powered-by {
      display: flex;
      align-items: center;
    }
  }
}
</style>
